import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send, Square } from 'react-feather';
import AssistantMarkdown from '../components/AssistantMarkdown';
import {
  DEFAULT_UI_CONFIG,
  PublicAssistantMessage,
  PublicAssistantSource,
  PublicAssistantUiConfig,
  fetchPublicAssistantConfig,
  streamPublicAssistantReply,
} from '../services/publicAssistantApi';

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: PublicAssistantSource[];
  isError?: boolean;
}

const createId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Le chat s'affiche en pleine page dans un iframe, dont la largeur dépend du site
 * hôte. Sans cette colonne, les réponses longues s'étirent sur toute la fenêtre et
 * deviennent illisibles.
 */
const readingColumnSx = { width: '100%', maxWidth: 780, mx: 'auto' } as const;

const TypingDots: React.FC = () => (
  <Stack direction="row" spacing={0.6} sx={{ py: 0.5 }}>
    {[0, 1, 2].map((index) => (
      <Box
        key={index}
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'var(--brand-primary)',
          opacity: 0.7,
          animation: 'assistant-typing 1.2s infinite ease-in-out',
          animationDelay: `${index * 0.15}s`,
          '@keyframes assistant-typing': {
            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.35 },
            '30%': { transform: 'translateY(-4px)', opacity: 1 },
          },
        }}
      />
    ))}
  </Stack>
);

const PublicChat: React.FC = () => {
  const [ui, setUi] = useState<PublicAssistantUiConfig>(DEFAULT_UI_CONFIG);
  const [maxMessageChars, setMaxMessageChars] = useState(2000);
  const [isAvailable, setIsAvailable] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    document.body.style.margin = '0';

    void (async () => {
      try {
        const config = await fetchPublicAssistantConfig();
        setUi(config.ui);
        setMaxMessageChars(config.maxMessageChars);
        setIsAvailable(config.enabled);
      } catch {
        setIsAvailable(false);
      } finally {
        setIsReady(true);
      }
    })();

    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const hasConversation = messages.length > 0;

  const send = useCallback(
    (rawText: string) => {
      const text = rawText.trim().slice(0, maxMessageChars);
      if (!text || isStreaming) return;

      const userMessage: DisplayMessage = { id: createId(), role: 'user', content: text };
      const assistantId = createId();

      // L'historique transmis au backend n'inclut pas la bulle de réponse en cours.
      const history: PublicAssistantMessage[] = [...messages, userMessage]
        .filter((message) => !message.isError && message.content.trim())
        .map(({ role, content }) => ({ role, content }));

      setMessages((previous) => [
        ...previous,
        userMessage,
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      setInput('');
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const updateAssistant = (updater: (message: DisplayMessage) => DisplayMessage) =>
        setMessages((previous) =>
          previous.map((message) => (message.id === assistantId ? updater(message) : message))
        );

      void streamPublicAssistantReply(
        history,
        {
          onSources: (sources) => updateAssistant((message) => ({ ...message, sources })),
          onDelta: (text) =>
            updateAssistant((message) => ({ ...message, content: message.content + text })),
          onError: (errorMessage) =>
            updateAssistant((message) => ({
              ...message,
              content: message.content || errorMessage,
              isError: !message.content,
            })),
          onDone: () => {
            setIsStreaming(false);
            abortRef.current = null;
            inputRef.current?.focus();
          },
        },
        controller.signal
      );
    },
    [isStreaming, maxMessageChars, messages]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send(input);
    }
  };

  const suggestions = useMemo(
    () => (hasConversation ? [] : ui.suggestedQuestions.filter(Boolean)),
    [hasConversation, ui.suggestedQuestions]
  );

  if (!isReady) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isAvailable) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          L'assistant est momentanément indisponible. Merci de réessayer plus tard.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        }}
      >
        <Box sx={readingColumnSx}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {ui.title}
          </Typography>
          {ui.subtitle && (
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.25 }}>
              {ui.subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
        <Box
          sx={{
            ...readingColumnSx,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <AssistantMarkdown text={ui.greeting} />
            </Box>
          </Box>

          {messages.map((message, index) => {
            const isUser = message.role === 'user';
            const isLast = index === messages.length - 1;
            const isPending = isLast && isStreaming && !message.content && !isUser;

            return (
              <Box
                key={message.id}
                sx={{
                  alignSelf: isUser ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: isUser
                      ? 'var(--brand-primary-15)'
                      : 'rgba(255, 255, 255, 0.06)',
                    border: isUser
                      ? '1px solid var(--brand-primary-30)'
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    color: message.isError ? 'error.main' : 'text.primary',
                  }}
                >
                  {isPending ? (
                    <TypingDots />
                  ) : isUser ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  ) : (
                    <AssistantMarkdown text={message.content} />
                  )}
                </Box>

                {ui.showSources && !isUser && !!message.sources?.length && !!message.content && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}
                  >
                    {message.sources.map((source) => (
                      <Chip
                        key={`${message.id}-${source.title}`}
                        label={source.title}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          color: 'text.disabled',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}

          {!!suggestions.length && (
            <Stack spacing={1} sx={{ mt: 1, alignItems: 'flex-start' }}>
              {suggestions.map((question) => (
                <Chip
                  key={question}
                  label={question}
                  onClick={() => send(question)}
                  sx={{
                    height: 'auto',
                    py: 0.75,
                    borderRadius: 2,
                    color: 'var(--brand-primary)',
                    backgroundColor: 'var(--brand-primary-10)',
                    border: '1px solid var(--brand-primary-30)',
                    '& .MuiChip-label': { whiteSpace: 'normal', px: 1.25 },
                    '&:hover': { backgroundColor: 'var(--brand-primary-19)' },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        }}
      >
        <Box sx={readingColumnSx}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={5}
              size="small"
              autoComplete="off"
              value={input}
              placeholder={ui.placeholder}
              onChange={(event) => setInput(event.target.value.slice(0, maxMessageChars))}
              onKeyDown={handleKeyDown}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: 'var(--brand-primary-30)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--brand-primary)' },
                },
              }}
            />
            <IconButton
              onClick={() => (isStreaming ? stop() : send(input))}
              disabled={!isStreaming && !input.trim()}
              aria-label={isStreaming ? 'Arrêter la réponse' : 'Envoyer'}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'var(--brand-primary)',
                color: 'var(--brand-on-primary)',
                '&:hover': { backgroundColor: 'var(--brand-primary-hover)' },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {isStreaming ? <Square size={16} /> : <Send size={18} />}
            </IconButton>
          </Stack>

          {ui.disclaimer && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: 'text.disabled', fontSize: '0.7rem' }}
            >
              {ui.disclaimer}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PublicChat;
